import React from 'react';
import { Button, Col, Form, Input, InputNumber, Row, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
// Helpers
import { formatCount, formatMoney } from 'pages/Events/helpers';
import { DEFAULT_MAX_PER_ORDER } from '../../helpers';
// Styles
import styles from './TiersEditor.module.scss';

const { Text } = Typography;

export const TIER_LABEL_MAX_LENGTH = 120;
export const MAX_TIER_CAPACITY = 1000000;
export const MAX_TIERS = 200;

const hasAtMostTwoDecimals = (value: number): boolean =>
  Math.round(value * 100) === value * 100;

type TiersEditorProps = {
  minTicketPrice: number;
  maxTicketPrice: number;
  /** Tiers already saved on the event, which only count toward the limit. */
  savedTierCount?: number;
  requireOne?: boolean;
  addLabel?: string;
};

const TiersEditor = ({
  minTicketPrice,
  maxTicketPrice,
  savedTierCount = 0,
  requireOne = false,
  addLabel = 'Add tier',
}: TiersEditorProps): JSX.Element => (
  <Form.List
    name="tiers"
    rules={[
      {
        validator: (_, tiers?: unknown[]) =>
          !requireOne || (tiers && tiers.length > 0)
            ? Promise.resolve()
            : Promise.reject(new Error('Add at least one ticket tier')),
      },
    ]}
  >
    {(fields, { add, remove }, { errors }) => (
      <div className={styles.root}>
        {fields.map((field, index) => (
          <div key={field.key} className={styles.tier} data-testid="tier-row">
            <div className={styles.tierHeader}>
              <Text strong>Tier {savedTierCount + index + 1}</Text>
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                aria-label={`Remove tier ${savedTierCount + index + 1}`}
                onClick={() => remove(field.name)}
              />
            </div>
            <Row gutter={12}>
              <Col xs={24} md={9}>
                <Form.Item
                  name={[field.name, 'label']}
                  label="Name"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: 'Name the tier',
                    },
                    {
                      max: TIER_LABEL_MAX_LENGTH,
                      message: `At most ${TIER_LABEL_MAX_LENGTH} characters`,
                    },
                  ]}
                >
                  <Input placeholder="e.g. General Admission" />
                </Form.Item>
              </Col>
              <Col xs={12} md={5}>
                <Form.Item
                  name={[field.name, 'price']}
                  label="Price ($)"
                  rules={[
                    { required: true, message: 'Set a price' },
                    {
                      type: 'number',
                      min: minTicketPrice,
                      max: maxTicketPrice,
                      message: `Between ${formatMoney(
                        minTicketPrice
                      )} and ${formatMoney(maxTicketPrice)}`,
                    },
                    {
                      validator: (_, value) =>
                        value === undefined ||
                        value === null ||
                        hasAtMostTwoDecimals(value)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error('Use at most two decimals')
                            ),
                    },
                  ]}
                >
                  <InputNumber
                    className={styles.number}
                    min={0}
                    step={1}
                    placeholder="25.00"
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={5}>
                <Form.Item
                  name={[field.name, 'capacity']}
                  label="Quantity"
                  rules={[
                    { required: true, message: 'Set a quantity' },
                    {
                      type: 'integer',
                      min: 1,
                      max: MAX_TIER_CAPACITY,
                      message: `A whole number from 1 to ${formatCount(
                        MAX_TIER_CAPACITY
                      )}`,
                    },
                  ]}
                >
                  <InputNumber
                    className={styles.number}
                    min={1}
                    step={1}
                    precision={0}
                    placeholder="250"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={5}>
                <Form.Item
                  name={[field.name, 'maxPerOrder']}
                  label="Per order"
                  tooltip="The most tickets of this tier one order can hold."
                  rules={[
                    {
                      type: 'integer',
                      min: 1,
                      max: 100,
                      message: 'From 1 to 100',
                    },
                  ]}
                >
                  <InputNumber
                    className={styles.number}
                    min={1}
                    max={100}
                    precision={0}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name={[field.name, 'description']}
                  label="Description"
                  className={styles.description}
                >
                  <Input placeholder="Optional: what this tier includes" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        ))}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => add({ maxPerOrder: DEFAULT_MAX_PER_ORDER })}
          disabled={savedTierCount + fields.length >= MAX_TIERS}
          block
        >
          {addLabel}
        </Button>
        <Form.ErrorList errors={errors} />
      </div>
    )}
  </Form.List>
);

export default TiersEditor;
